// kmp vuex module
import cookies from '@/services/cookies'

const COMMON_HEADERS = {
  'Content-Type': 'application/json',
  'X-Application-Platform': 'iOS 18.4',
  Accept: 'application/json',
}

function getApiUrl () {
  const apiRoot = cookies.get('API_ROOT') || process.env.VUE_APP_API_ROOT
  return `${apiRoot}/api/mobile-apps/v2_25`
}

// Важно: apiInstance хранится вне state, чтобы избежать ошибок Vuex
// (vuex не разрешает мутировать объекты в state вне mutation, а apiInstance мутируется внутри сторонней библиотеки)
let apiInstance = null

export default {
  namespaced: true,

  state: () => ({
    api: null,
    repoRequest: null,
    serviceRequest: null,
    mealRequest: null,
    discounts: [],
    menu: null,
    isInitialized: false,
  }),

  getters: {
    menu: state => state.menu,
  },

  mutations: {
    setApi (state, payload) {
      state.api = payload
    },
    setRepoRequest (state, payload) {
      state.repoRequest = payload
    },
    setServiceRequest (state, payload) {
      state.serviceRequest = payload
    },
    setMealRequest (state, payload) {
      state.mealRequest = payload
    },
    setDiscounts (state, payload) {
      state.discounts = payload
    },
    setMenu (state, payload) {
      state.menu = payload
    },
    setInitialized (state, bool) {
      state.isInitialized = bool
    },
  },

  actions: {
    updateKmpApi ({ commit, dispatch }, payload) {
      if (!payload) {
        return
      }
      commit('setApi', payload)
      apiInstance = payload.getInstance()
      dispatch('initializeMenuLibrary')
    },

    async initializeMenuLibrary ({ commit, state }) {
      if (!apiInstance || state.isInitialized) {
        return
      }

      const getMenuCallback = async (params) => {
        const clientToken = window.localStorage.getItem('client_token')
        const headers = { ...COMMON_HEADERS }
        if (clientToken) {
          headers['X-Client-Token'] = clientToken
        } else {
          delete headers['X-Client-Token']
        }

        const menuUrl = clientToken ? '/customization-info/custom-menu' : '/customization-info/custom-menu-no-auth'

        try {
          const response = await fetch(getApiUrl() + menuUrl, {
            method: 'POST',
            headers,
            body: JSON.stringify(params),
          })

          if (!response.ok) {
            const errorText = await response.text()
            console.error(`Ошибка API ${response.status}: ${errorText}`)
            return JSON.stringify({
              success: false,
              error: `HTTP ошибка ${response.status}: ${errorText}`,
            })
          }

          const data = await response.json()
          return JSON.stringify(data)
        } catch (error) {
          console.error('Ошибка при запросе меню:', error)
          return JSON.stringify({
            success: false,
            error: error.message || 'Неизвестная ошибка',
          })
        }
      }

      const loadCategoriesCallback = async () => {
        try {
          const response = await fetch(getApiUrl() + '/libs/pack-filter-categories', {
            method: 'GET',
            headers: COMMON_HEADERS,
          })
          const data = await response.json()
          return JSON.stringify(data)
        } catch (error) {
          console.error('Ошибка при запросе категорий:', error)
          return JSON.stringify({
            success: false,
            error: error.message || 'Неизвестная ошибка',
          })
        }
      }

      const loadPropertiesCallback = async () => {
        try {
          const response = await fetch(getApiUrl() + '/libs/packs-properties', {
            method: 'GET',
            headers: COMMON_HEADERS,
          })
          const data = await response.json()
          return JSON.stringify(data)
        } catch (error) {
          console.error('Ошибка при запросе свойств:', error)
          return JSON.stringify({
            success: false,
            error: error.message || 'Неизвестная ошибка',
          })
        }
      }

      try {
        await apiInstance.initializeLibrary(
          getMenuCallback,
          loadCategoriesCallback,
          loadPropertiesCallback,
        )
        commit('setInitialized', true)
      } catch (error) {
        console.error(error)
      }
    },

    // Создаем запрос репозитория с указанными параметрами
    async createMenuRepositoryRequest ({ commit }, payload) {
      if (!apiInstance) {
        return
      }

      const { cityId, menuType, firstDeliveryDate, dayTime, filters, pricePlanId, menuDays } = payload

      const repoRequest = await apiInstance.createMenuRepositoryRequest(
        cityId,
        menuType,
        firstDeliveryDate,
        dayTime,
        filters || [],
        pricePlanId,
        menuDays,
      )

      commit('setRepoRequest', repoRequest)
    },

    async createMenuServiceRequest ({ state, commit }) {
      if (!apiInstance) {
        return
      }

      const serviceRequest = apiInstance.createMenuServiceRequest(
        state.repoRequest,
        state.discounts,
        false, // isFreeCustomEnabled
      )
      commit('setServiceRequest', serviceRequest)
    },

    // Создаем скидки
    createDiscounts ({ commit }, discounts) {
      const discountsArray = []
      discounts.forEach((discount) => {
        discountsArray.push(apiInstance.createDiscount(discount.discountType, discount.discountValue))
      })
      commit('setDiscounts', discountsArray)
    },

    async getMenu ({ state, dispatch, commit }, payload) {
      if (!apiInstance) {
        return
      }

      if (!state.isInitialized) {
        await dispatch('initializeMenuLibrary')
      }

      try {
        await dispatch('createMenuRepositoryRequest', payload)
        await dispatch('createDiscounts', payload.discounts)
        await dispatch('createMenuServiceRequest')

        const menuResult = await apiInstance.getMenu(state.serviceRequest)
        console.log('kmpMenuResult', menuResult)
        commit('setMenu', menuResult)
        return menuResult
      } catch (error) {
        console.error('Ошибка при запросе меню:', error)
        return null
      }
    },

    async createMealRequest ({ state, commit }, payload) {
      if (!apiInstance) {
        return
      }

      const mealRequest = apiInstance.createMealRequest(state.serviceRequest, payload.mealDate, payload.mealNumber)
      commit('setMealRequest', mealRequest)
    },

    async getAlternativePacks ({ state, dispatch }, payload) {
      if (!apiInstance) {
        return
      }

      try {
        if (!state.repoRequest || !state.serviceRequest) {
          await dispatch('createMenuRepositoryRequest', payload)
          await dispatch('createDiscounts', payload.discounts)
          await dispatch('createMenuServiceRequest')
        }

        await dispatch('createMealRequest', payload)
        const alternatives = await apiInstance.getPackReplaces(state.mealRequest, payload.packIndex)
        return alternatives
      } catch (error) {
        console.error('Ошибка при запросе альтернативных паков:', error)
        return null
      }
    },

    async removePackFromMenu ({ state, dispatch, commit }, payload) {
      if (!apiInstance) {
        return
      }

      try {
        if (!state.repoRequest || !state.serviceRequest) {
          await dispatch('createMenuRepositoryRequest', payload)
          await dispatch('createDiscounts', payload)
          await dispatch('createMenuServiceRequest')
        }

        await dispatch('createMealRequest', payload)

        const removeCommand = apiInstance.createDeleteMealCustomCommand(state.mealRequest, payload.packId)
        const menuResult = await apiInstance.applyCustomCommand(removeCommand)
        commit('setMenu', menuResult)
        return menuResult
      } catch (error) {
        console.error('Ошибка при удалении пака из меню:', error)
        return null
      }
    },

    async addPackToMenu ({ state, dispatch, commit }, payload) {
      if (!apiInstance) {
        return
      }

      try {
        if (!state.repoRequest || !state.serviceRequest) {
          await dispatch('createMenuRepositoryRequest', payload)
          await dispatch('createDiscounts', payload)
          await dispatch('createMenuServiceRequest')
        }

        await dispatch('createMealRequest', payload)
        const addCommand = apiInstance.createAddMealCustomCommand(state.mealRequest, payload.packId)
        const menu = await apiInstance.applyCustomCommand(addCommand)
        commit('setMenu', menu)
        return menu
      } catch (error) {
        console.error('Ошибка при добавлении пака в меню:', error)
        return null
      }
    },

    async replacePackInMenu ({ state, dispatch, commit }, payload) {
      if (!apiInstance) {
        return
      }

      try {
        if (!state.repoRequest || !state.serviceRequest) {
          await dispatch('createMenuRepositoryRequest', payload)
          await dispatch('createDiscounts', payload)
          await dispatch('createMenuServiceRequest')
        }

        await dispatch('createMealRequest', payload)
        const replaceCommand = apiInstance.createReplaceMealCustomCommand(
          state.mealRequest,
          payload.oldPackId,
          payload.packIndex,
          payload.newPackId,
          '',
        )
        const menu = await apiInstance.applyCustomCommand(replaceCommand)
        commit('setMenu', menu)
        return menu
      } catch (error) {
        console.error('Ошибка при замене пака в меню:', error)
        return null
      }
    },

    async resetMenuCustomization ({ state, dispatch, commit }, payload) {
      if (!apiInstance) {
        return
      }

      try {
        if (!state.repoRequest || !state.serviceRequest) {
          await dispatch('createMenuRepositoryRequest', payload)
          await dispatch('createDiscounts', payload)
          await dispatch('createMenuServiceRequest')
        }
        const menuResult = await apiInstance.resetUserCustom(state.serviceRequest)
        commit('setMenu', menuResult)
        console.log('menuResult', menuResult)
        return menuResult
      } catch (error) {
        console.error('Ошибка при сбросе кастомизации меню:', error)
        return null
      }
    },
  },
}
